/**
 * Shared Sales Funnel table rows — used by BOTH the Reports-page Sales Team
 * Report renderer and the Forecast page's SalesFunnelTable so the two views
 * render team-grouped funnel data identically (same columns, formats,
 * subtotal styling, and appointment drill-down).
 *
 * Column order (11 cols): Salesperson | Leads Visits Appts Sales |
 * Visits/Lead Sales/Visit Sales/Lead | Active Leads | Next 3 Next 5.
 *
 * The individual cell GROUPS are exported so the Sales Team Report's
 * week-column variant (renderers/funnel/WeekFunnelRows.tsx) can compose the
 * same cells around its extra period columns instead of duplicating ~55 lines
 * of <td> markup. This file exists precisely because that duplication had
 * already drifted once; keep every cell defined here exactly once.
 *
 * MemberRow / TotalsRow / TeamHeaderRow / EmptyTeamRow keep their original
 * signatures and produce byte-identical DOM — the added props are optional and
 * default to the previous output, so the Forecast page is unaffected.
 */

import React from 'react';
import { SalesFunnelRow, SalesFunnelTotals } from '../../services/api';

export const FUNNEL_COL_COUNT = 11;

/** "2026-07-13" → "Mon 7/13" (parsed as a local calendar date). */
export function dayLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
  return `${dow} ${m}/${d}`;
}

/** "2026-07-13" → "7/13". */
export function shortDate(dateStr: string): string {
  const [, m, d] = dateStr.split('-').map(Number);
  return `${m}/${d}`;
}

export function fmtPct(v: number | null): React.ReactNode {
  if (v === null) return <span className="text-gray-400">—</span>;
  return `${v.toFixed(1)}%`;
}

export function fmtCount(v: number): React.ReactNode {
  return v > 0 ? v : <span className="text-gray-400">—</span>;
}

/**
 * Unit counts, which are fractional on split deals (each rep on a
 * 2-salesperson deal gets 0.5). Renders `8.5` but `12` rather than `12.0`, and
 * clamps float noise so a summed bucket cannot print
 * "2.9999999999999996". Used by the week columns; the 11 base columns keep
 * fmtCount so their output is unchanged.
 */
export function fmtUnits(v: number): string {
  const rounded = Math.round(v * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function shortReason(reason: string): string {
  if (reason === 'Sales Appointment') return 'Sales';
  return reason;
}

type CellVariant = 'member' | 'totals';

/**
 * Leads / Visits / Appts / Sales. In the Sales Team Report's range mode these
 * four are the whole-range totals; the per-week columns sit to their left.
 */
export const FunnelCountCells: React.FC<{
  leads: number;
  visits: number;
  apptsPast: number;
  sales: number;
  variant: CellVariant;
}> = ({ leads, visits, apptsPast, sales, variant }) => (
  variant === 'member' ? (
    <>
      <td className="px-3 py-2 text-right tabular-nums text-gray-900 border-l border-gray-100">{fmtCount(leads)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-900">{fmtCount(visits)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-900">{fmtCount(apptsPast)}</td>
      <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{fmtCount(sales)}</td>
    </>
  ) : (
    <>
      <td className="px-3 py-2 text-right tabular-nums border-l border-gray-200">{leads}</td>
      <td className="px-3 py-2 text-right tabular-nums">{visits}</td>
      <td className="px-3 py-2 text-right tabular-nums">{apptsPast}</td>
      <td className="px-3 py-2 text-right tabular-nums">{sales}</td>
    </>
  )
);

/**
 * Visits/Lead, Sales/Visit, Sales/Lead. `optionalClass` lets the report hide
 * these in print at high week-column counts (see index.css
 * `.funnel-print-optional`); the Forecast page never passes it.
 */
export const FunnelConversionCells: React.FC<{
  visitsPerLeadPct: number | null;
  salesPerVisitPct: number | null;
  salesPerLeadPct: number | null;
  variant: CellVariant;
  optionalClass?: string;
}> = ({ visitsPerLeadPct, salesPerVisitPct, salesPerLeadPct, variant, optionalClass = '' }) => {
  const opt = optionalClass ? ` ${optionalClass}` : '';
  return variant === 'member' ? (
    <>
      <td className={`px-3 py-2 text-right tabular-nums text-gray-600 border-l border-gray-100${opt}`}>{fmtPct(visitsPerLeadPct)}</td>
      <td className={`px-3 py-2 text-right tabular-nums text-gray-600${opt}`}>{fmtPct(salesPerVisitPct)}</td>
      <td className={`px-3 py-2 text-right tabular-nums text-gray-600${opt}`}>{fmtPct(salesPerLeadPct)}</td>
    </>
  ) : (
    <>
      <td className={`px-3 py-2 text-right tabular-nums border-l border-gray-200${opt}`}>{fmtPct(visitsPerLeadPct)}</td>
      <td className={`px-3 py-2 text-right tabular-nums${opt}`}>{fmtPct(salesPerVisitPct)}</td>
      <td className={`px-3 py-2 text-right tabular-nums${opt}`}>{fmtPct(salesPerLeadPct)}</td>
    </>
  );
};

/**
 * Active Leads — a point-in-time count as of NOW, not a window figure. On
 * historic reports it cannot be reconstructed, so aggregates render an em dash
 * rather than a summed 0 that would misread as "zero active leads".
 */
export const FunnelActiveLeadsCell: React.FC<{
  value: number | null;
  unavailable?: boolean;
  variant: CellVariant;
}> = ({ value, unavailable = false, variant }) => (
  variant === 'member' ? (
    <td className="px-3 py-2 text-right tabular-nums text-gray-900 border-l border-gray-100">
      {value !== null ? fmtCount(value) : <span className="text-gray-400">—</span>}
    </td>
  ) : (
    <td className="px-3 py-2 text-right tabular-nums border-l border-gray-200">
      {unavailable ? <span className="text-gray-400">—</span> : value}
    </td>
  )
);

/** Next 3 / Next 5 scheduled appointments. */
export const FunnelApptNextCells: React.FC<{
  apptsNext3: number;
  apptsNext5: number;
  variant: CellVariant;
  optionalClass?: string;
}> = ({ apptsNext3, apptsNext5, variant, optionalClass = '' }) => {
  const opt = optionalClass ? ` ${optionalClass}` : '';
  return variant === 'member' ? (
    <>
      <td className={`px-3 py-2 text-right tabular-nums text-gray-900 border-l border-gray-100${opt}`}>{fmtCount(apptsNext3)}</td>
      <td className={`px-3 py-2 text-right tabular-nums text-gray-900${opt}`}>{fmtCount(apptsNext5)}</td>
    </>
  ) : (
    <>
      <td className={`px-3 py-2 text-right tabular-nums border-l border-gray-200${opt}`}>{apptsNext3}</td>
      <td className={`px-3 py-2 text-right tabular-nums${opt}`}>{apptsNext5}</td>
    </>
  );
};

/**
 * The expanded appointment-times drill-down beneath a member row.
 *
 * `colSpan` MUST be the table's real column count — the week-column variant
 * passes periods.length + 11. Leaving it at 11 there would collapse this grid
 * under the first 11 columns and visually shatter the row.
 */
export const ApptDetailsRow: React.FC<{
  row: SalesFunnelRow;
  nextDates: string[];
  reportDate: string;
  reportDateIsToday: boolean;
  colSpan?: number;
}> = ({ row, nextDates, reportDate, reportDateIsToday, colSpan = FUNNEL_COL_COUNT }) => (
  <tr>
    <td colSpan={colSpan} className="px-3 py-2 pl-12 bg-gray-50/60">
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${nextDates.length + 1}, 1fr)` }}
      >
        {[reportDate, ...nextDates].map((date) => {
          const details = row.apptDetailsByDate?.[date] || [];
          return (
            <div key={date} className="min-w-0">
              <div className="text-gray-500 font-semibold mb-0.5" style={{ fontSize: '10px' }}>
                {date === reportDate && reportDateIsToday ? `Today ${shortDate(date)}` : dayLabel(date)}
              </div>
              {details.length === 0 ? (
                <div className="text-gray-300" style={{ fontSize: '10px' }}>—</div>
              ) : (
                details.map((appt, i) => (
                  <div
                    key={i}
                    className="text-gray-600 truncate"
                    style={{ fontSize: '11px' }}
                    title={appt.subject || undefined}
                  >
                    {appt.time}{' '}
                    <span className="text-gray-400">({shortReason(appt.reason)})</span>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </td>
  </tr>
);

/** True when the row has any scheduled appointments to drill into. */
export function hasApptDetails(row: SalesFunnelRow): boolean {
  return !!row.apptDetailsByDate && Object.keys(row.apptDetailsByDate).length > 0;
}

/**
 * Totals-style row used for team subtotals, location totals, and grand
 * totals. On historic (past-month) reports Active Leads cannot be
 * reconstructed — every member row is null and the summed 0 would misread as
 * "zero active leads", so aggregates render an em dash instead.
 *
 * `firstColClassName` exists for the report's sticky first column; omitted it
 * reproduces the original markup exactly.
 */
export const TotalsRow: React.FC<{
  label: string;
  totals: SalesFunnelTotals;
  emphasis?: boolean;
  activeLeadsUnavailable?: boolean;
  firstColClassName?: string;
  optionalClass?: string;
}> = ({
  label,
  totals,
  emphasis = false,
  activeLeadsUnavailable = false,
  firstColClassName = '',
  optionalClass = '',
}) => (
  <tr className={emphasis
    ? 'border-t-2 border-gray-300 bg-gray-100 font-semibold text-gray-900'
    : 'border-t border-gray-200 bg-gray-50 font-medium text-gray-700'}>
    <td className={`px-3 py-2 text-left${firstColClassName ? ` ${firstColClassName}` : ''}`}>{label}</td>
    <FunnelCountCells
      leads={totals.leads}
      visits={totals.visits}
      apptsPast={totals.apptsPast}
      sales={totals.sales}
      variant="totals"
    />
    <FunnelConversionCells
      visitsPerLeadPct={totals.visitsPerLeadPct}
      salesPerVisitPct={totals.salesPerVisitPct}
      salesPerLeadPct={totals.salesPerLeadPct}
      variant="totals"
      optionalClass={optionalClass}
    />
    <FunnelActiveLeadsCell
      value={totals.activeLeads}
      unavailable={activeLeadsUnavailable}
      variant="totals"
    />
    <FunnelApptNextCells
      apptsNext3={totals.apptsNext3}
      apptsNext5={totals.apptsNext5}
      variant="totals"
      optionalClass={optionalClass}
    />
  </tr>
);

/**
 * One salesperson's funnel row (indented under its team header), with the
 * expandable appointment-times drill-down when the row has scheduled
 * appointments (click the ▸).
 */
export const MemberRow: React.FC<{
  row: SalesFunnelRow;
  rowId: string;
  isOpen: boolean;
  onToggle: (rowId: string) => void;
  nextDates: string[];
  reportDate: string;
  reportDateIsToday: boolean;
}> = ({ row, rowId, isOpen, onToggle, nextDates, reportDate, reportDateIsToday }) => {
  const hasDetails = hasApptDetails(row);
  return (
    <>
      <tr
        className={`border-t border-gray-100 ${hasDetails ? 'cursor-pointer hover:bg-gray-50 select-none' : ''}`}
        onClick={hasDetails ? () => onToggle(rowId) : undefined}
      >
        <td className="px-3 py-2 text-gray-900 whitespace-nowrap pl-6">
          {hasDetails && (
            <span className="inline-block w-3 text-gray-400">{isOpen ? '▾' : '▸'}</span>
          )}
          {row.name}
        </td>
        <FunnelCountCells
          leads={row.leads}
          visits={row.visits}
          apptsPast={row.apptsPast}
          sales={row.sales}
          variant="member"
        />
        <FunnelConversionCells
          visitsPerLeadPct={row.visitsPerLeadPct}
          salesPerVisitPct={row.salesPerVisitPct}
          salesPerLeadPct={row.salesPerLeadPct}
          variant="member"
        />
        <FunnelActiveLeadsCell value={row.activeLeads} variant="member" />
        <FunnelApptNextCells apptsNext3={row.apptsNext3} apptsNext5={row.apptsNext5} variant="member" />
      </tr>
      {hasDetails && isOpen && (
        <ApptDetailsRow
          row={row}
          nextDates={nextDates}
          reportDate={reportDate}
          reportDateIsToday={reportDateIsToday}
        />
      )}
    </>
  );
};

/**
 * Team header band ("Team {manager}" + member count; Unassigned bucket) —
 * the blue divider row above each team's member rows.
 */
export const TeamHeaderRow: React.FC<{
  managerId: string;
  managerName: string;
  rowCount: number;
  colSpan?: number;
}> = ({
  managerId,
  managerName,
  rowCount,
  colSpan = FUNNEL_COL_COUNT,
}) => (
  <tr className="border-t-2 border-gray-200 bg-blue-50/60">
    <td colSpan={colSpan} className="px-3 py-1.5 font-semibold text-gray-800">
      {managerId ? `Team ${managerName}` : 'Unassigned'}
      <span className="ml-2 text-xs font-normal text-gray-500">
        ({rowCount} {rowCount === 1 ? 'salesperson' : 'salespeople'})
      </span>
    </td>
  </tr>
);

/** Placeholder row for a configured team with no funnel activity in the window. */
export const EmptyTeamRow: React.FC<{ colSpan?: number; label?: string }> = ({
  colSpan = FUNNEL_COL_COUNT,
  label = 'No funnel activity from this team in the window.',
}) => (
  <tr className="border-t border-gray-100">
    <td colSpan={colSpan} className="px-3 py-2 pl-6 text-sm text-gray-400 italic">
      {label}
    </td>
  </tr>
);
